/**
 * Duration tiers of a face's read pricelist — the merge between what the
 * PMS says today (`dim_pricelist_tier`, canonical) and what the operator
 * picked when the face was last saved (`repricing_rate_card_tier`).
 *
 * PRD 01 §3 "Tiers de duración" and "Drift a lo largo del tiempo":
 * - A tier that still exists keeps its `reference_lor`.
 * - A new tier appears to be filled; the documented default is the top of
 *   the range (`duration_to`), which is the LOR that best represents the
 *   bracket. It is prefilled and flagged `new` so the operator sees it.
 * - The LAST bracket of every pricelist is open-ended: `duration_to` is
 *   `null`, meaning "60 days and up". It has no top, so its default is
 *   its own lower bound and its options run from there. PRD 01 §3.
 * - A tier that disappeared is shown struck through and purged on save.
 * - `reference_lor` must satisfy `duration_from ≤ ref ≤ duration_to`; the
 *   server rejects anything else with `tier_reference_lor_out_of_range`.
 *
 * Pure module: no React, no fetch. Unit-tested with `node --test`.
 */

/** How many choices to offer on the open-ended bracket, which has no top. */
const OPEN_ENDED_OPTIONS = 15

export const isOpenEnded = (tier) => tier.duration_to === null || tier.duration_to === undefined

/** `1-3 d` for a closed bracket, `60+ d` for the open-ended one. */
export const tierLabel = (tier) => (isOpenEnded(tier)
	? `${tier.duration_from}+`
	: `${tier.duration_from}-${tier.duration_to}`)

export const defaultReferenceLor = (tier) => (isOpenEnded(tier)
	? tier.duration_from
	: tier.duration_to)

const keyOf = (t) => (isOpenEnded(t) ? `${t.duration_from}+` : `${t.duration_from}-${t.duration_to}`)

/**
 * `mergeTiers(canonical, saved)` → rows for the editor, canonical order
 * first, then the disappeared ones.
 *
 * Each row: `{ duration_from, duration_to, reference_lor, status }`
 * with `status` in `'ok' | 'new' | 'gone'`.
 */
export function mergeTiers(canonical = [], saved = []) {
	const savedByKey = new Map(saved.map(t => [keyOf(t), t]))
	const rows = []
	for (const c of [...canonical].sort((a, b) => a.duration_from - b.duration_from)) {
		const hit = savedByKey.get(keyOf(c))
		rows.push({
			duration_from: c.duration_from,
			duration_to: c.duration_to,
			reference_lor: hit ? hit.reference_lor : defaultReferenceLor(c),
			status: hit ? 'ok' : 'new',
		})
		savedByKey.delete(keyOf(c))
	}
	for (const leftover of savedByKey.values()) {
		rows.push({ ...leftover, status: 'gone' })
	}
	return rows
}

/** True when the saved picks no longer match the canonical structure. */
export function hasDrift(canonical = [], saved = []) {
	if (!canonical.length) return false
	const a = canonical.map(keyOf).sort().join(',')
	const b = saved.map(keyOf).sort().join(',')
	return a !== b
}

/** Editor rows → what gets persisted: live tiers only, ordered. */
export function tiersToSave(rows = []) {
	return rows
		.filter(r => r.status !== 'gone')
		.sort((a, b) => a.duration_from - b.duration_from)
		.map(({ duration_from, duration_to, reference_lor }) => ({
			duration_from, duration_to, reference_lor,
		}))
}

/** Valid `reference_lor` choices for a tier — every integer in its range. */
export function referenceLorOptions(tier) {
	const out = []
	const top = isOpenEnded(tier)
		? tier.duration_from + OPEN_ENDED_OPTIONS - 1
		: tier.duration_to
	for (let lor = tier.duration_from; lor <= top; lor++) out.push(lor)
	return out
}

/** Replace one tier's `reference_lor`, clamped to its own range. */
export function setReferenceLor(rows, key, lor) {
	return rows.map((r) => {
		if (keyOf(r) !== key) return r
		const atLeast = Math.max(lor, r.duration_from)
		// No upper clamp on the open-ended bracket: `Math.min(x, null)`
		// is 0, which would silently reset the operator's choice.
		return { ...r, reference_lor: isOpenEnded(r) ? atLeast : Math.min(atLeast, r.duration_to) }
	})
}

export const tierKey = keyOf
